import { Component, OnInit } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { PractitionerSpecialtyAutoCompleteItem } from "src/app/ui/models/domains/auto-complete/practitioner-specialty-auto-complete-item";
import { PractitionerSpecialtyAutoCompleteProvider } from "src/app/ui/models/domains/auto-complete/practitioner-specialty-auto-complete-provider";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { Practitioner } from "src/app/ui/models/domains/pbm/practitioner-management/practitioner";
import { PractitionerSpecialty } from "src/app/ui/models/domains/pbm/practitioner-management/practitioner-specialty";
import { PractitionerStatus } from "src/app/ui/models/domains/pbm/practitioner-management/practitioner-status";
import { StateAcronym } from "src/app/ui/models/domains/pbm/state/state-acronym";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { PractitionerManagementService } from "src/app/ui/services/pbm/practitioner-management.service";
import { StateService } from "src/app/ui/services/pbm/state.service";
import { UserService } from "src/app/ui/services/user.service";
import { Toast } from "../../../toast/toast";
import { PractitionerFormValidator } from "../practitioner-form.validator";
import { PractitionerForm } from "../practitioner.form";

@Component({
  selector: "register-practitioner",
  templateUrl: "./register-practitioner.component.html",
  styleUrls: ["./register-practitioner.component.scss"],
})
export class RegisterPractitionerComponent implements OnInit {
  private practitionerForm: PractitionerForm;
  private states: StateAcronym[];
  private practitionerStatusList: PractitionerStatus[];
  private selectedPractitionerSpecialty: PractitionerSpecialtyAutoCompleteItem;
  private resetAutoCompleteEvent = new Event("resetSearchControl");
  public autoCompleteProvider: PractitionerSpecialtyAutoCompleteProvider;

  constructor(
    private featureViewService: FeatureViewService,
    private stateService: StateService,
    private router: Router,
    private practitionerService: PractitionerManagementService,
    private loadingService: LoadingService,
    private toast: Toast,
    private userService: UserService,
  ) {
    this.autoCompleteProvider = new PractitionerSpecialtyAutoCompleteProvider(this.practitionerService);
    this.practitionerForm = new PractitionerForm();
    this.states = [];
    this.practitionerStatusList = [];
  }

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.RegisterPractitioner, PageViews.PractitionerManagement);
    const loadingRef = this.loadingService.beginLoading();
    try {
      this.states = await this.stateService.getStates();
      this.practitionerStatusList = await this.practitionerService.getAllPractitionerStatus();
    } finally {
      loadingRef.closeLoading();
    }
  }

  public getPractitionerForm(): UntypedFormGroup {
    return this.practitionerForm.getFormGroup();
  }

  public getStates(): StateAcronym[] {
    return this.states;
  }

  public getPractitionerStatusList(): PractitionerStatus[] {
    return this.practitionerStatusList;
  }

  public getSpecialties(): PractitionerSpecialty[] {
    return this.practitionerForm.getFormGroup().value.specialties;
  }

  public goToSearchPractitioner(): void {
    this.router.navigate([RoutePath.SearchPractitioner]);
  }

  public async registerPractitioner(): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    const validator = new PractitionerFormValidator(this.practitionerForm);
    if (validator.isFormValid()) {
      try {
        const practitioner: Practitioner = this.practitionerForm.parseFormGroup();
        const practitionerKey = await this.practitionerService.registerPractitioner(practitioner);
        this.toast.showSuccessToast("Médico registrado com sucesso");
        this.router.navigate([RoutePath.EditPractitioner, practitionerKey]);
      } catch (httpErrorResponse) {
        let errorMessage = "Erro ao registrar médico(a)";
        if (httpErrorResponse.error) {
          errorMessage = httpErrorResponse.error.Data.ErrorMessage;
        }
        this.toast.showWarningToast(errorMessage);
      } finally {
        loadingRef.closeLoading();
      }
    } else {
      loadingRef.closeLoading();
      this.toast.showWarningToast(validator.getErrorMessage());
    }
  }

  public getAutoCompleteValue(value: PractitionerSpecialtyAutoCompleteItem): void {
    this.selectedPractitionerSpecialty = value;
  }

  public addSpecialty(): void {
    if (this.selectedPractitionerSpecialty) {
      if (this.getSpecialties().length === 0) {
        this.getSpecialties().push(this.selectedPractitionerSpecialty.getPractitionerSpecialty());
        document.dispatchEvent(this.resetAutoCompleteEvent);
        return;
      }
      const index = this.getSpecialties().findIndex((item) => item.getKey() === this.selectedPractitionerSpecialty.getKey());
      if (index < 0) {
        this.getSpecialties().push(this.selectedPractitionerSpecialty.getPractitionerSpecialty());
        document.dispatchEvent(this.resetAutoCompleteEvent);
      } else {
        this.toast.showWarningToast("Especialização já está selecionada.");
        document.dispatchEvent(this.resetAutoCompleteEvent);
      }
    }
  }

  public deleteSpecialty(specialty: PractitionerSpecialty): void {
    const index = this.getSpecialties().indexOf(specialty);
    this.getSpecialties().splice(index, 1);
  }

  public hasAccessToRegisterPractitioner(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.RegisterPractitioner);
  }
}
